import { useHasPermission } from '@app/core/auth/authorization/hooks/useHasPermission';
import { useTooltipWithHotkey } from '@app/core/hotkeys/useTooltipWithHotkey';
import { FragmentIcon } from '@app/modules/fragments/components/FragmentIcon';
import { streamHotkeys } from '@app/modules/logger/screens/stream/stream-hotkeys';
import { Menu } from '@mi-front/ui';
import {
  AudioFileOutlined as AudioFileOutlinedIcon,
  Close as CloseIcon,
  Link as LinkIcon,
  PauseRounded as PauseIcon,
  PlayArrowRounded as PlayIcon,
  VideoFileOutlined as VideoFileOutlinedIcon,
} from '@mui/icons-material';
import noop from 'lodash/noop';
import { useIntl } from 'react-intl';
import { isVideoStation } from '../../../../../../../../core/stations/helpers';
import { useActiveStation } from '../../../../../../../../core/stations/hooks/useActiveStation';
import { quickSharePermissions } from './quick-share-permissions';

interface Props {
  readonly loadingAudio: boolean;
  readonly play: null | (() => void);
  readonly pause: null | (() => void);
  readonly createFragment: null | (() => void);
  readonly downloadMedia: () => void;
  readonly downloadAudioMedia: () => void;
  readonly isPreparingVideoDownload?: boolean;
  readonly isPreparingAudioDownload?: boolean;
  readonly copyQuickShareUrl: () => void;
  readonly clear: () => void;
}

export function SelectionMenuDefaultP(p: Props) {
  const { play, createFragment, downloadMedia, downloadAudioMedia, clear, copyQuickShareUrl } = p;
  const { $t } = useIntl();
  const activeStation = useActiveStation();
  const isActiveVideoStation = !!activeStation && isVideoStation(activeStation);

  return (
    <Menu
      className="el-selection-menu"
      actions={[
        {
          icon: <PlayIcon />,
          onClick: play || noop,
          tooltip: useTooltipWithHotkey(
            {
              id: 'logger.streamScreen.selectionMenu.play',
              defaultMessage: 'Reproducir selección',
            },
            streamHotkeys.get('playback', 'PLAY_PAUSE'),
          ),
          enabled: !!play,
          loading: p.loadingAudio,
        },
        {
          icon: <PauseIcon />,
          onClick: p.pause || noop,
          tooltip: useTooltipWithHotkey(
            {
              id: 'logger.streamScreen.cursorMenu.pause',
              defaultMessage: 'Pausar',
            },
            streamHotkeys.get('playback', 'PLAY_PAUSE'),
          ),
          enabled: !!p.pause,
          loading: p.loadingAudio,
        },
        {
          icon: <FragmentIcon fontSize="small" />,
          onClick: createFragment || noop,
          tooltip: useTooltipWithHotkey(
            {
              id: 'logger.streamScreen.selectionMenu.createFragment',
              defaultMessage: 'Crear fragmento',
            },
            streamHotkeys.get('selection', 'CREATE_FRAGMENT'),
          ),
          enabled: !!createFragment,
          btnProps: {
            'data-testid': 'create-fragment-btn',
          },
        },
        {
          icon: <LinkIcon fontSize="small" />,
          onClick: copyQuickShareUrl,
          tooltip: $t({
            id: 'logger.streamScreen.selectionMenu.copyQuickShare',
            defaultMessage: 'Copiar URL Quick Share',
          }),
          enabled: !!useHasPermission(quickSharePermissions.QuickShare_Share),
        },
        {
          icon: <VideoFileOutlinedIcon fontSize="small" />,
          onClick: downloadMedia,
          tooltip: useTooltipWithHotkey(
            {
              id: 'logger.streamScreen.selectionMenu.downloadVideo',
              defaultMessage: 'Descargar video',
            },
            streamHotkeys.get('selection', 'DOWNLOAD_SELECTION'),
          ),
          enabled: isActiveVideoStation,
          loading: p.isPreparingVideoDownload ?? false,
        },
        {
          icon: <AudioFileOutlinedIcon fontSize="small" />,
          onClick: downloadAudioMedia,
          tooltip: useTooltipWithHotkey(
            {
              id: 'logger.streamScreen.selectionMenu.downloadAudio',
              defaultMessage: 'Descargar audio',
            },
            isActiveVideoStation
              ? streamHotkeys.get('selection', 'DOWNLOAD_AUDIO_SELECTION_VIDEO_ONLY')
              : streamHotkeys.get('selection', 'DOWNLOAD_SELECTION'),
          ),
          enabled: !!activeStation,
          loading: p.isPreparingAudioDownload ?? false,
        },
        {
          icon: <CloseIcon fontSize="small" />,
          onClick: clear,
          tooltip: useTooltipWithHotkey(
            {
              id: 'logger.streamScreen.selectionMenu.unselect',
              defaultMessage: 'Deseleccionar',
            },
            streamHotkeys.get('selection', 'CLEAR_SELECTION'),
          ),
        },
      ]}
    />
  );
}
